// ============================================================
// 경매 단기매매 AI 투자 판단 시뮬레이터 — 계산 엔진 v2
// 5단계 연산 + AI 100점 종합 점수 + 손익분기점 + 민감도 분석
// ============================================================

export interface SimulatorInput {
  equity: number;
  ltv: number;
  loanRate: number;
  holdingMonths: number;
  appraisalValue: number;
  bidRate: number;
  saleRate: number;
  acquisitionTaxRate: number;
  legalCost: number;
  evictionCost: number;
  repairCost: number;
  cleanCost: number;
  brokerRate: number;
  propertyType: string;
  occupantType: string;
  recentTransactions: number;
}

export interface CostBreakdown {
  acquisitionCost: number;
  improvementCost: number;
  holdingCost: number;
  sellingCost: number;
  totalCost: number;
  monthlyInterest: number;
}

export interface ScoreBreakdown {
  bidDiscount: number;
  roeScore: number;
  liquidity: number;
  cashFlow: number;
  eviction: number;
  total: number;
  discountRate: number;
}

export interface SimulatorResult {
  maxBidPrice: number;
  referenceBidPrice: number;
  loanAmount: number;
  expectedSalePrice: number;
  surfaceProfit: number;
  surfaceROE: number;
  netProfit: number;
  actualROE: number;
  annualizedROE: number;
  monthlyAvgProfit: number;
  costs: CostBreakdown;
  breakevenSalePrice: number;
  breakevenSaleRate: number;
  score: ScoreBreakdown;
  grade: 'S' | 'A' | 'B' | 'C';
  gradeLabel: string;
  gradeColor: string;
  recommendation: string;
  recommendationDetail: string;
}

export interface SensitivityPoint {
  paramValue: number;
  paramLabel: string;
  result: SimulatorResult;
}

export interface SensitivityAnalysis {
  paramName: string;
  currentValue: number;
  unit: string;
  points: SensitivityPoint[];
}

/** 5단계 연산 파이프라인 */
export function calculateSimulator(input: SimulatorInput): SimulatorResult {
  const {
    equity, ltv, loanRate, holdingMonths,
    appraisalValue, bidRate, saleRate,
    acquisitionTaxRate, legalCost, evictionCost, repairCost, cleanCost, brokerRate,
    propertyType, occupantType, recentTransactions,
  } = input;

  const maxBidPrice = Math.round(equity / (1 - ltv));
  const loanAmount = Math.round(maxBidPrice * ltv);
  const referenceBidPrice = Math.round(appraisalValue * bidRate);
  const expectedSalePrice = Math.round(appraisalValue * saleRate);
  const surfaceProfit = expectedSalePrice - maxBidPrice;
  const surfaceROE = equity > 0 ? Math.round((surfaceProfit / equity) * 100 * 10) / 10 : 0;

  const acquisitionCost = Math.round(maxBidPrice * acquisitionTaxRate) + legalCost;
  const improvementCost = repairCost + evictionCost + cleanCost;
  const holdingCost = Math.round(maxBidPrice * ltv * (loanRate / 100) * (holdingMonths / 12));
  const monthlyInterest = Math.round(holdingCost / Math.max(1, holdingMonths));
  const sellingCost = Math.round(expectedSalePrice * brokerRate);
  const totalCost = acquisitionCost + improvementCost + holdingCost + sellingCost;
  const netProfit = surfaceProfit - totalCost;

  const actualROE = equity > 0 ? Math.round((netProfit / equity) * 100 * 10) / 10 : 0;
  const annualizedROE = holdingMonths > 0 ? Math.round(actualROE * (12 / holdingMonths) * 10) / 10 : 0;
  const monthlyAvgProfit = holdingMonths > 0 ? Math.round(netProfit / holdingMonths) : 0;

  // 손익분기점: netProfit = 0 이 되는 매도가
  const fixedCostsExBroker = maxBidPrice + acquisitionCost + improvementCost + holdingCost;
  const breakevenSalePrice = brokerRate < 1
    ? Math.round(fixedCostsExBroker / (1 - brokerRate))
    : fixedCostsExBroker;
  const breakevenSaleRate = appraisalValue > 0
    ? Math.round((breakevenSalePrice / appraisalValue) * 1000) / 10
    : 0;

  const score = calculateAIScore(input, actualROE, maxBidPrice, appraisalValue, netProfit, holdingCost, holdingMonths);
  const gradeResult = determineGrade(score.total, actualROE);

  return {
    maxBidPrice,
    referenceBidPrice,
    loanAmount,
    expectedSalePrice,
    surfaceProfit,
    surfaceROE,
    netProfit,
    actualROE,
    annualizedROE,
    monthlyAvgProfit,
    costs: { acquisitionCost, improvementCost, holdingCost, sellingCost, totalCost, monthlyInterest },
    breakevenSalePrice,
    breakevenSaleRate,
    score,
    ...gradeResult,
  };
}

/** 민감도 분석: 특정 파라미터 ±20% 범위에서 ROE·등급 변화 */
export function analyzeSensitivity(
  baseInput: SimulatorInput,
  paramKey: keyof SimulatorInput,
  steps: number = 11,
): SensitivityAnalysis {
  const baseValue = baseInput[paramKey] as number;
  const isPercentParam = ["ltv", "bidRate", "saleRate", "acquisitionTaxRate", "brokerRate"].includes(paramKey);
  const unitMap: Record<string, string> = {
    equity: "억원", ltv: "%", loanRate: "%", holdingMonths: "개월",
    appraisalValue: "억원", bidRate: "%", saleRate: "%",
    acquisitionTaxRate: "%", legalCost: "만원", evictionCost: "만원",
    repairCost: "만원", cleanCost: "만원", brokerRate: "%",
  };
  const unit = unitMap[paramKey] || "";

  const range = isPercentParam ? 0.15 : 0.2;
  const points: SensitivityPoint[] = [];

  for (let i = 0; i < steps; i++) {
    const factor = 1 - range + (2 * range * i) / (steps - 1);
    let paramValue: number;
    let paramLabel: string;

    if (isPercentParam) {
      paramValue = Math.round(baseValue * factor * 10000) / 10000;
      paramValue = Math.max(0.001, Math.min(0.999, paramValue));
      paramLabel = (paramValue * 100).toFixed(1) + "%";
    } else {
      paramValue = Math.round(baseValue * factor);
      if (["legalCost", "evictionCost", "repairCost", "cleanCost"].includes(paramKey)) {
        paramValue = Math.round(paramValue / 10000) * 10000;
        paramValue = Math.max(0, paramValue);
        paramLabel = (paramValue / 10000).toFixed(0) + "만원";
      } else if (["equity", "appraisalValue"].includes(paramKey)) {
        paramValue = Math.max(1, paramValue);
        paramLabel = (paramValue / 100000000).toFixed(1) + "억원";
      } else {
        paramValue = Math.max(1, paramValue);
        paramLabel = String(Math.round(paramValue));
      }
    }

    const testInput: SimulatorInput = { ...baseInput, [paramKey]: paramValue };
    const result = calculateSimulator(testInput);
    points.push({ paramValue, paramLabel, result });
  }

  return { paramName: paramKey, currentValue: baseValue, unit, points };
}

/** AI 종합 투자 점수 (100점 만점) */
function calculateAIScore(
  input: SimulatorInput,
  actualROE: number,
  maxBidPrice: number,
  appraisalValue: number,
  netProfit: number,
  holdingCost: number,
  holdingMonths: number,
): ScoreBreakdown {
  const { propertyType, occupantType, recentTransactions } = input;

  const discountRate = appraisalValue > 0 ? ((appraisalValue - maxBidPrice) / appraisalValue) * 100 : 0;
  let bidDiscount: number;
  if (discountRate >= 30) bidDiscount = 30;
  else if (discountRate >= 25) bidDiscount = 27;
  else if (discountRate >= 20) bidDiscount = 24;
  else if (discountRate >= 15) bidDiscount = 20;
  else if (discountRate >= 10) bidDiscount = 15;
  else if (discountRate >= 5) bidDiscount = 10;
  else if (discountRate >= 0) bidDiscount = 5;
  else bidDiscount = 0;

  let roeScore: number;
  if (actualROE >= 80) roeScore = 25;
  else if (actualROE >= 60) roeScore = 20;
  else if (actualROE >= 40) roeScore = 15;
  else if (actualROE >= 20) roeScore = 10;
  else if (actualROE >= 10) roeScore = 5;
  else roeScore = 0;

  let liquidityBase = 10;
  switch (propertyType) {
    case "아파트": liquidityBase = 14; break;
    case "오피스텔": liquidityBase = 12; break;
    case "다세대/연립": liquidityBase = 10; break;
    case "단독주택": liquidityBase = 8; break;
    case "상가": liquidityBase = 6; break;
    default: liquidityBase = 10;
  }
  const txBonus = recentTransactions >= 10 ? 6 : recentTransactions >= 5 ? 4 : recentTransactions >= 2 ? 2 : 0;
  const liquidity = Math.min(20, liquidityBase + txBonus);

  const monthlyInterest = holdingCost / Math.max(1, holdingMonths);
  const dscr = monthlyInterest > 0 ? netProfit / holdingCost : (netProfit > 0 ? 15 : 0);
  let cashFlow: number;
  if (dscr >= 5) cashFlow = 15;
  else if (dscr >= 3) cashFlow = 13;
  else if (dscr >= 2) cashFlow = 11;
  else if (dscr >= 1.5) cashFlow = 9;
  else if (dscr >= 1) cashFlow = 7;
  else if (dscr >= 0.5) cashFlow = 4;
  else if (dscr >= 0) cashFlow = 2;
  else cashFlow = 0;

  let eviction: number;
  switch (occupantType) {
    case "없음": eviction = 10; break;
    case "채무자": eviction = 7; break;
    case "임차인(무권리)": eviction = 5; break;
    case "임차인(대항력)": eviction = 3; break;
    case "유치권자": eviction = 1; break;
    default: eviction = 5;
  }

  const total = bidDiscount + roeScore + liquidity + cashFlow + eviction;

  return {
    bidDiscount: Math.round(bidDiscount),
    roeScore: Math.round(roeScore),
    liquidity: Math.round(liquidity),
    cashFlow: Math.round(cashFlow),
    eviction: Math.round(eviction),
    total: Math.round(total),
    discountRate: Math.round(discountRate * 10) / 10,
  };
}

function determineGrade(totalScore: number, actualROE: number): {
  grade: 'S' | 'A' | 'B' | 'C';
  gradeLabel: string;
  gradeColor: string;
  recommendation: string;
  recommendationDetail: string;
} {
  if (totalScore >= 90 && actualROE >= 80) {
    return {
      grade: 'S',
      gradeLabel: 'S급 — 적극 입찰',
      gradeColor: '#10B981',
      recommendation: '적극 입찰',
      recommendationDetail: 'AI 종합 판단 결과 투자 가치가 매우 높습니다. 목표 낙찰가를 상향 조정하여도 충분한 수익 여력이 있습니다.',
    };
  }
  if (totalScore >= 80 && actualROE >= 40) {
    return {
      grade: 'A',
      gradeLabel: 'A급 — 표준 입찰',
      gradeColor: '#F59E0B',
      recommendation: '표준 입찰',
      recommendationDetail: 'AI 종합 판단 결과 안정적인 투자처입니다. 기준 입찰가를 준수하여 입찰에 참여하세요.',
    };
  }
  if (totalScore >= 70 && actualROE >= 20) {
    return {
      grade: 'B',
      gradeLabel: 'B급 — 보수적 입찰',
      gradeColor: '#F97316',
      recommendation: '보수적 입찰',
      recommendationDetail: 'AI 종합 판단 결과 수익 여력이 제한적입니다. 보수적 하향 조정을 고려하세요.',
    };
  }
  return {
    grade: 'C',
    gradeLabel: 'C급 — 입찰 포기',
    gradeColor: '#EF4444',
    recommendation: '입찰 포기',
    recommendationDetail: 'AI 종합 판단 결과 투자 가치가 매우 낮습니다. 더 나은 물건을 찾는 것이 현명합니다.',
  };
}

/** 시나리오 프리셋 */
export interface ScenarioPreset {
  id: string;
  label: string;
  icon: string;
  description: string;
  overrides: Partial<SimulatorInput>;
}

export const SCENARIO_PRESETS: ScenarioPreset[] = [
  {
    id: "conservative",
    label: "보수적",
    icon: "ri-shield-check-line",
    description: "안전마진 최우선, 낮은 LTV·높은 할인율",
    overrides: {
      ltv: 0.40,
      bidRate: 0.70,
      saleRate: 0.88,
      loanRate: 5.5,
      holdingMonths: 9,
      repairCost: 8000000,
      evictionCost: 3000000,
    },
  },
  {
    id: "neutral",
    label: "중립적",
    icon: "ri-scales-3-line",
    description: "균형 잡힌 현실적 시나리오",
    overrides: {
      ltv: 0.60,
      bidRate: 0.80,
      saleRate: 0.93,
      loanRate: 4.5,
      holdingMonths: 6,
      repairCost: 5000000,
      evictionCost: 2000000,
    },
  },
  {
    id: "aggressive",
    label: "공격적",
    icon: "ri-rocket-line",
    description: "고레버리지·고수익 추구",
    overrides: {
      ltv: 0.80,
      bidRate: 0.90,
      saleRate: 0.97,
      loanRate: 3.8,
      holdingMonths: 4,
      repairCost: 2000000,
      evictionCost: 1000000,
    },
  },
];

export function getDefaultInput(): SimulatorInput {
  return {
    equity: 200000000,
    ltv: 0.6,
    loanRate: 4.5,
    holdingMonths: 6,
    appraisalValue: 500000000,
    bidRate: 0.80,
    saleRate: 0.95,
    acquisitionTaxRate: 0.046,
    legalCost: 1000000,
    evictionCost: 2000000,
    repairCost: 5000000,
    cleanCost: 300000,
    brokerRate: 0.005,
    propertyType: "아파트",
    occupantType: "없음",
    recentTransactions: 8,
  };
}

/** 민감도 분석용 파라미터 목록 */
export const SENSITIVITY_PARAMS: Array<{ key: keyof SimulatorInput; label: string; icon: string }> = [
  { key: "bidRate", label: "낙찰가율", icon: "ri-percent-line" },
  { key: "saleRate", label: "매도가율", icon: "ri-line-chart-line" },
  { key: "loanRate", label: "대출금리", icon: "ri-bank-line" },
  { key: "holdingMonths", label: "보유기간", icon: "ri-calendar-line" },
  { key: "ltv", label: "LTV", icon: "ri-funds-line" },
];